import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, TrendingUp, Users, FileCheck2, Calendar, DollarSign, Layers,
  MousePointerClick, Presentation, ClipboardCheck, Video, Award, Sparkles,
  Star, Bell, ChevronRight, CalendarRange, Wrench, Rocket,
} from "lucide-react";
import { clientApi } from "../lib/api";
import { useClientAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const fmtBRL = (n) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(n || 0);
const fmtInt = (n) => new Intl.NumberFormat("pt-BR").format(n || 0);
const fmtDec = (n, d = 2) => new Intl.NumberFormat("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d }).format(n || 0);

// Department pertinence: which KPIs belong to which departments
// Empty department filter = show all
const DEPT_KPIS = {
  "Atendimento":              ["campaigns", "meetings", "demands_open", "demands_done"],
  "Planejamento/Estratégia":  ["plans", "campaigns", "products"],
  "Criação":                  ["pieces_delivered", "budgets_prod", "budgets_prod_approved"],
  "Mídia":                    ["media_quotes", "media_quotes_approved", "invested_media"],
  "Tráfego Pago":             ["leads", "cpl_total", "invested_paid_traffic"],
  "Produção":                 ["budgets_prod", "budgets_prod_approved", "invested_production_graphic", "invested_production_av", "invested_production_events", "invested_production_actions"],
};

const SERVICE_KPIS = [
  { id: "pieces_delivered", label: "Peças Entregues", icon: FileCheck2, color: "from-green-500/20 to-green-500/5", accent: "text-green-400", get: (s) => s.counts.pieces_delivered, fmt: fmtInt },
  { id: "plans", label: "Planejamentos", icon: ClipboardCheck, color: "from-teal-500/20 to-teal-500/5", accent: "text-teal-400", get: (s) => s.counts.plans, fmt: fmtInt },
  { id: "meetings", label: "Reuniões (Atendimento)", icon: Calendar, color: "from-pink-500/20 to-pink-500/5", accent: "text-pink-400", get: (s) => s.counts.meetings, fmt: fmtInt },
  { id: "budgets_prod", label: "Orçamentos de Produção", icon: Layers, color: "from-indigo-500/20 to-indigo-500/5", accent: "text-indigo-400", get: (s) => s.counts.budgets_prod, fmt: fmtInt },
  { id: "budgets_prod_approved", label: "Orçamentos Aprovados", icon: FileCheck2, color: "from-emerald-500/20 to-emerald-500/5", accent: "text-emerald-400", get: (s) => s.counts.budgets_prod_approved, fmt: fmtInt },
  { id: "media_quotes", label: "Cotações de Mídia", icon: Presentation, color: "from-cyan-500/20 to-cyan-500/5", accent: "text-cyan-400", get: (s) => s.counts.media_quotes, fmt: fmtInt },
  { id: "media_quotes_approved", label: "Cotações Aprovadas", icon: FileCheck2, color: "from-lime-500/20 to-lime-500/5", accent: "text-lime-400", get: (s) => s.counts.media_quotes_approved, fmt: fmtInt },
  { id: "campaigns", label: "Campanhas", icon: Rocket, color: "from-blue-500/20 to-blue-500/5", accent: "text-blue-400", get: (s) => s.counts.campaigns, fmt: fmtInt },
];

const PERF_KPIS = [
  { id: "leads", label: "Leads Gerados", icon: MousePointerClick, color: "from-orange-500/20 to-orange-500/5", accent: "text-accent-orange", get: (s) => s.counts.leads, fmt: fmtInt },
  { id: "cpl_total", label: "CPL Médio", icon: DollarSign, color: "from-emerald-500/20 to-emerald-500/5", accent: "text-emerald-400", get: (s) => s.financial.cpl_total, fmt: (n) => fmtBRL(n) },
  { id: "units_sold", label: "Unidades Vendidas", icon: Award, color: "from-amber-500/20 to-amber-500/5", accent: "text-accent-yellow", get: (s) => s.counts.units_sold, fmt: fmtInt },
  { id: "products", label: "Produtos Ativos", icon: Building2, color: "from-purple-500/20 to-purple-500/5", accent: "text-purple-400", get: (s) => s.counts.products, fmt: fmtInt },
];

export default function ClientDashboard() {
  const { user } = useClientAuth();
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [products, setProducts] = useState([]);
  const [campaignFilter, setCampaignFilter] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [meta, setMeta] = useState(null);
  const [google, setGoogle] = useState(null);
  const [nps, setNps] = useState([]);
  const [npsForm, setNpsForm] = useState({ open: false, id: null, score: null, comment: "" });

  useEffect(() => {
    (async () => {
      const [{ data: s }, { data: c }, { data: p }, { data: m }, { data: g }, { data: n }] = await Promise.all([
        clientApi.get("/dashboard", { params: { campaign_id: campaignFilter || undefined, department: deptFilter || undefined } }),
        clientApi.get("/campaigns"),
        clientApi.get("/products"),
        clientApi.get("/integrations/meta").catch(() => ({ data: null })),
        clientApi.get("/integrations/google").catch(() => ({ data: null })),
        clientApi.get("/nps").catch(() => ({ data: [] })),
      ]);
      setStats(s); setCampaigns(c); setProducts(p); setMeta(m); setGoogle(g); setNps(n);
    })();
  }, [campaignFilter, deptFilter]);

  // Filter KPIs by department pertinence
  const relevantIds = deptFilter ? new Set(DEPT_KPIS[deptFilter] || []) : null;
  const filteredService = SERVICE_KPIS.filter((k) => !relevantIds || relevantIds.has(k.id));
  const filteredPerf = PERF_KPIS.filter((k) => !relevantIds || relevantIds.has(k.id));

  const openNps = nps.filter((n) => !n.responded && n.status !== "closed");
  const respondedNps = nps.filter((n) => n.responded);
  const avgNps = respondedNps.length > 0 ? Math.round(respondedNps.reduce((s, n) => s + (Number(n.score) || 0), 0) / respondedNps.length * 10) / 10 : null;

  const investmentBreakdown = stats ? [
    { label: "Mídia", value: stats.financial.invested_media, key: "invested_media" },
    { label: "Tráfego Pago", value: stats.financial.invested_paid_traffic, key: "invested_paid_traffic" },
    { label: "Produção Gráfica", value: stats.financial.invested_production_graphic, key: "invested_production_graphic" },
    { label: "Produção AV", value: stats.financial.invested_production_av, key: "invested_production_av" },
    { label: "Eventos", value: stats.financial.invested_production_events, key: "invested_production_events" },
    { label: "Ações", value: stats.financial.invested_production_actions, key: "invested_production_actions" },
  ].filter((r) => !relevantIds || relevantIds.has(r.key)) : [];
  const investmentTotal = investmentBreakdown.reduce((s, r) => s + (r.value || 0), 0);

  const submitNps = async () => {
    if (npsForm.score == null) { toast.error("Escolha uma nota."); return; }
    try {
      await clientApi.post(`/nps/${npsForm.id}/respond`, { score: npsForm.score, comment: npsForm.comment });
      toast.success("Obrigado pela sua resposta!");
      setNpsForm({ open: false, id: null, score: null, comment: "" });
      const { data } = await clientApi.get("/nps");
      setNps(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-8" data-testid="cliente-dashboard-page">
      {/* HEADER */}
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-accent-orange mb-1">Bem-vindo(a)</div>
          <h1 className="font-display text-3xl md:text-5xl leading-tight">
            {user?.name || user?.company || user?.email}<span className="text-accent-orange">.</span>
          </h1>
          <div className="mt-2 text-sm text-neutral-400 flex flex-wrap gap-x-6 gap-y-1">
            {stats?.user_summary?.responsible_am && <span>Atendimento: <b className="text-white">{stats.user_summary.responsible_am}</b></span>}
            {stats?.user_summary?.client_since && <span>Cliente desde: <b className="text-white">{new Date(stats.user_summary.client_since).toLocaleDateString("pt-BR")}</b></span>}
            {avgNps != null && <span>NPS Médio: <b className="text-accent-yellow">{avgNps}</b></span>}
          </div>
        </div>
      </div>

      {/* NPS PENDING CARDS */}
      <AnimatePresence>
        {openNps.map((n) => (
          <motion.div
            key={n.id} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="relative border-2 border-accent-yellow bg-accent-yellow/10 rounded-xl p-5 overflow-hidden"
            data-testid={`nps-pending-${n.id}`}
          >
            <div className="absolute -top-1 -right-1 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-yellow opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-accent-yellow"></span>
            </div>
            <div className="flex justify-between items-start gap-4 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-accent-yellow font-bold mb-1 flex items-center gap-1.5">
                  <motion.div animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.5, repeat: Infinity }}><Bell className="w-3 h-3" /></motion.div>
                  Pesquisa NPS Pendente
                </div>
                <div className="font-display text-xl">{n.title || "Como você avalia nosso serviço?"}</div>
                <div className="text-sm text-neutral-300 mt-1">{n.subtitle || "Sua resposta leva menos de 30 segundos e nos ajuda muito."}</div>
              </div>
              <button onClick={() => setNpsForm({ open: true, id: n.id, score: null, comment: "" })} data-testid={`nps-respond-${n.id}`} className="bg-accent-yellow hover:bg-white text-black font-bold px-5 py-2.5 rounded-full text-sm transition whitespace-nowrap">
                Responder pesquisa →
              </button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* FILTERS */}
      <div className="grid md:grid-cols-4 gap-3 border border-white/10 rounded-xl p-4 bg-white/[0.02]" data-testid="cliente-filters">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Campanha</label>
          <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} data-testid="filter-campaign" className="mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm">
            <option value="">Visão Geral (todas)</option>
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Departamento</label>
          <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} data-testid="filter-department" className="mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm">
            <option value="">Todos (visão geral)</option>
            {Object.keys(DEPT_KPIS).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1"><CalendarRange className="w-3 h-3" /> Data inicial</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" className="mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1"><CalendarRange className="w-3 h-3" /> Data final</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" className="mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm" />
        </div>
      </div>

      {/* KPIs — SERVIÇO vs PERFORMANCE */}
      {stats && (
        <>
          {filteredService.length > 0 && (
            <section data-testid="kpi-service-section">
              <div className="flex items-center gap-3 mb-4">
                <Wrench className="w-4 h-4 text-neutral-400" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-accent-orange font-bold">KPIs de Serviço</div>
                  <div className="text-[11px] text-neutral-500">Entregas da agência para você (Atendimento, Planejamento, Criação, Mídia, Produção)</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {filteredService.map((k, i) => (
                  <motion.div
                    key={k.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                    className={`relative overflow-hidden border border-white/10 rounded-xl p-4 md:p-5 bg-gradient-to-br ${k.color}`}
                    data-testid={`kpi-service-${k.id}`}
                  >
                    <div className={`w-9 h-9 rounded-lg bg-black/40 flex items-center justify-center mb-3 ${k.accent}`}>
                      <k.icon className="w-4 h-4" />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">{k.label}</div>
                    <div className="font-display text-2xl md:text-3xl">{k.fmt(k.get(stats))}</div>
                  </motion.div>
                ))}
              </div>
            </section>
          )}

          {filteredPerf.length > 0 && (
            <section data-testid="kpi-performance-section">
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="w-4 h-4 text-accent-yellow" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-accent-yellow font-bold">KPIs de Performance</div>
                  <div className="text-[11px] text-neutral-500">Resultados de mercado — leads, vendas, ROI</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {filteredPerf.map((k, i) => (
                  <motion.div
                    key={k.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                    className={`relative overflow-hidden border-2 border-accent-yellow/40 rounded-xl p-4 md:p-5 bg-gradient-to-br ${k.color}`}
                    data-testid={`kpi-perf-${k.id}`}
                  >
                    <div className={`w-9 h-9 rounded-lg bg-black/40 flex items-center justify-center mb-3 ${k.accent}`}>
                      <k.icon className="w-4 h-4" />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">{k.label}</div>
                    <div className="font-display text-2xl md:text-3xl">{k.fmt(k.get(stats))}</div>
                  </motion.div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* FINANCIAL */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="border border-white/10 rounded-xl p-6 bg-white/[0.02]">
          <div className="text-xs uppercase tracking-widest text-accent-orange mb-4">Financeiro (por Produto)</div>
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-green-500/20 rounded-lg p-4 bg-green-500/5">
              <div className="text-[10px] uppercase tracking-widest text-green-400 mb-1">Pago</div>
              <div className="font-display text-2xl">{fmtBRL(stats?.financial?.paid)}</div>
            </div>
            <div className="border border-red-500/20 rounded-lg p-4 bg-red-500/5">
              <div className="text-[10px] uppercase tracking-widest text-red-400 mb-1">Devido</div>
              <div className="font-display text-2xl">{fmtBRL(stats?.financial?.due)}</div>
            </div>
          </div>
        </div>

        <div className="border border-white/10 rounded-xl p-6 bg-white/[0.02]">
          <div className="flex justify-between items-baseline mb-4">
            <div className="text-xs uppercase tracking-widest text-accent-orange">Investimento em Mídia & Produção</div>
            <div className="font-display text-xl">{fmtBRL(investmentTotal)}</div>
          </div>
          <div className="space-y-2">
            {investmentBreakdown.map((r) => {
              const pct = investmentTotal > 0 ? Math.round((r.value / investmentTotal) * 100) : 0;
              return (
                <div key={r.label}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-neutral-400">{r.label}</span>
                    <span className="font-semibold">{fmtBRL(r.value)} · {pct}%</span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-accent-orange to-accent-yellow" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* META + GOOGLE */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-white/10 rounded-xl p-5 bg-white/[0.02]">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-accent-orange">Meta Ads {meta?.source === "demo" && <span className="ml-1 text-neutral-500 lowercase">(demo)</span>}</div>
            <TrendingUp className="w-4 h-4 text-neutral-500" />
          </div>
          {meta && (
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><div className="font-display text-lg">{fmtInt(meta.data.impressions)}</div><div className="text-[10px] text-neutral-500 uppercase tracking-widest">Impressões</div></div>
              <div><div className="font-display text-lg">{fmtInt(meta.data.clicks)}</div><div className="text-[10px] text-neutral-500 uppercase tracking-widest">Cliques</div></div>
              <div><div className="font-display text-lg">{fmtBRL(meta.data.spend)}</div><div className="text-[10px] text-neutral-500 uppercase tracking-widest">Gasto</div></div>
            </div>
          )}
        </div>
        <div className="border border-white/10 rounded-xl p-5 bg-white/[0.02]">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-accent-orange">Google Analytics {google?.source === "demo" && <span className="ml-1 text-neutral-500 lowercase">(demo)</span>}</div>
            <Sparkles className="w-4 h-4 text-neutral-500" />
          </div>
          {google && (
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><div className="font-display text-lg">{fmtInt(google.data.sessions)}</div><div className="text-[10px] text-neutral-500 uppercase tracking-widest">Sessões</div></div>
              <div><div className="font-display text-lg">{fmtInt(google.data.users)}</div><div className="text-[10px] text-neutral-500 uppercase tracking-widest">Usuários</div></div>
              <div><div className="font-display text-lg">{fmtInt(google.data.conversions)}</div><div className="text-[10px] text-neutral-500 uppercase tracking-widest">Conversões</div></div>
            </div>
          )}
        </div>
      </div>

      {/* PRODUTOS */}
      <div>
        <div className="text-xs uppercase tracking-widest text-accent-orange mb-3">Seus Produtos Imobiliários</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => (
            <Link key={p.id} to={`/cliente/projetos?product=${p.id}`} className="group block border border-white/10 rounded-xl overflow-hidden hover:border-accent-orange transition-colors">
              <div className="aspect-video bg-neutral-900 relative overflow-hidden">
                {p.photo && <img src={p.photo} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
              </div>
              <div className="p-4">
                <div className="font-display text-lg leading-tight">{p.name}</div>
                <div className="text-xs text-neutral-500 mt-1">{p.type} · {p.city}</div>
                {p.vgv && <div className="mt-2 text-xs text-accent-yellow"><b>VGV:</b> {p.vgv}</div>}
              </div>
            </Link>
          ))}
          {products.length === 0 && <div className="col-span-full text-sm text-neutral-500 border border-dashed border-white/10 rounded-xl p-8 text-center">Nenhum produto cadastrado ainda.</div>}
        </div>
      </div>

      {/* NPS MODAL */}
      <AnimatePresence>
        {npsForm.open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="nps-modal">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setNpsForm({ open: false, id: null, score: null, comment: "" })} />
            <motion.div initial={{ y: 30, scale: 0.95 }} animate={{ y: 0, scale: 1 }} className="relative bg-[#141414] border border-white/10 rounded-2xl max-w-lg w-full p-6 md:p-8">
              <div className="text-xs uppercase tracking-widest text-accent-orange mb-1">Pesquisa NPS</div>
              <div className="font-display text-2xl mb-2">Em uma escala de 0 a 10, quanto você recomendaria a agência Places?</div>
              <p className="text-xs text-neutral-500 mb-5">Sua resposta é anônima para efeito de nota agregada, mas fica registrada no seu histórico com o time.</p>
              <div className="grid grid-cols-11 gap-1.5 mb-5">
                {Array.from({ length: 11 }).map((_, i) => (
                  <button key={i} onClick={() => setNpsForm((s) => ({ ...s, score: i }))} data-testid={`nps-score-${i}`}
                    className={`aspect-square rounded-lg font-bold text-sm border-2 transition ${npsForm.score === i ? "bg-accent-orange border-accent-orange text-black scale-110" : "border-white/10 hover:border-accent-orange text-neutral-300"}`}>
                    {i}
                  </button>
                ))}
              </div>
              <div className="flex justify-between text-[10px] text-neutral-500 uppercase tracking-widest mb-5">
                <span>Nada provável</span><span>Muito provável</span>
              </div>
              <textarea value={npsForm.comment} onChange={(e) => setNpsForm((s) => ({ ...s, comment: e.target.value }))} rows={3} placeholder="Deixe um comentário (opcional)" data-testid="nps-comment" className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm mb-4" />
              <div className="flex gap-2">
                <button onClick={() => setNpsForm({ open: false, id: null, score: null, comment: "" })} className="flex-1 py-3 border border-white/10 rounded-full text-sm">Cancelar</button>
                <button onClick={submitNps} disabled={npsForm.score == null} data-testid="nps-submit" className="flex-1 bg-accent-orange hover:bg-accent-yellow hover:text-black text-white font-bold py-3 rounded-full text-sm disabled:opacity-50">Enviar resposta</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
